import React, { useState, useEffect, memo } from 'react';
import { MapPin, Clock, Heart, Zap, Users, Car, Camera, Globe, Star, Box } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SmartImage } from './SmartImage';
import { Listing, Category } from '../types';
import { CATEGORY_CONFIG } from '../constants';
import { formatListingDate } from '../utils/dateFormat';
import { db } from '../services/db';
import { trackEvent } from '../services/tracking';
import { useUser } from '@clerk/clerk-react';
import { features } from '../services/features';
import { calculateDataCompleteness } from '../utils/listingQuality';
import { Sparkles } from 'lucide-react';

interface ListingCardProps {
  listing: Listing;
  compact?: boolean;
  priority?: boolean;
  onCompareToggle?: (id: string) => void;
  isComparing?: boolean;
}

const ListingCardComponent: React.FC<ListingCardProps> = ({ listing, compact = false, priority = false, onCompareToggle, isComparing = false }) => {
  const { user } = useUser();
  const { t } = useTranslation(["common", "home", "search", "listing", "profile", "add_listing", "admin"]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const hasGalleryUrl = !!listing.spaceDetails?.externalGalleryUrl;
  const allImages = listing.images || [];
  const validImages = allImages.filter(img => {
    if (!img || typeof img !== 'string') return false;
    const normalized = img.toLowerCase();
    return (
      !normalized.includes('external-gallery-placeholder') && 
      !normalized.includes('no-image') && 
      !normalized.includes('no-photo') && 
      !normalized.includes('no_image') && 
      !normalized.includes('camera-placeholder') &&
      !normalized.includes('broken-image') &&
      !(normalized.includes('images.unsplash.com') && normalized.includes('1519741497674-611481863552')) &&
      // If it looks like a website link rather than an image path (heuristic)
      !(normalized.startsWith('http') && !normalized.match(/\.(jpg|jpeg|png|webp|gif|svg|avif)/i) && !normalized.includes('images.unsplash.com'))
    );
  });
  const hasValidImages = validImages.length > 0;
  
  const getOptimizedUrl = (url: string) => {
    if (!url) return '';
    if (url.includes('images.unsplash.com') && !url.includes('w=')) {
      const width = compact ? 400 : 600;
      return `${url}${url.includes('?') ? '&' : '?'}w=${width}&q=80&auto=format`;
    }
    return url;
  };

  const getDisplayLocation = () => {
    // 1. Priority: structured city data
    const city = listing.spaceDetails?.address?.city;
    if (city) {
      return city;
    }

    // 2. Fallback: Parse raw location string
    let loc = (listing.location || '').replace(' (Demo)', '').replace(/,\s*Slovensko/i, '').replace(/Slovensko/i, '').trim();
    if (!loc) return '';

    // Heuristic: if it looks like a full address (has house number or zip)
    if (loc.match(/\d+/) || loc.split(' ').length > 2) {
      // Split by comma and take the last part
      const parts = loc.split(',').map(p => p.trim());
      const lastPart = parts[parts.length - 1];
      
      // Remove zip code
      const cityClean = lastPart
        .replace(/\d{3}\s?\d{2}/g, '') // 900 21
        .replace(/\d{5}/g, '')         // 90021
        .trim();
        
      if (cityClean && cityClean.length > 1) {
        return cityClean;
      }
    }

    return loc;
  };

  const displayLocation = getDisplayLocation();

  // Helper to format price using translation
  const getPriceLabel = () => {
    if (listing.spaceDetails?.priceType === 'konsumne') {
      return t('listing:price_contact');
    }
    if (typeof listing.spaceDetails?.pricePerHour === 'number' && listing.spaceDetails.pricePerHour > 0) {
      return t('listing:price_per_hour', { price: listing.spaceDetails.pricePerHour });
    }
    return (listing.price === 0 || !listing.price) ? t('listing:price_agreement') : t('listing:price_starting', { price: listing.price, currency: listing.currency || '€' });
  };

  const priceLabel = getPriceLabel();
  const completeness = calculateDataCompleteness(listing);
  const isAiReady = completeness >= 90;

  useEffect(() => {
    if (!isHovered) {
      setCurrentImageIndex(0);
      return;
    }
    if (!validImages || validImages.length <= 1) return;
    const randomDuration = 3500 + Math.random() * 2500;
    const intervalId = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % validImages.length);
    }, randomDuration);
    return () => clearInterval(intervalId);
  }, [validImages.length, isHovered]);

  useEffect(() => {
    const load = async () => {
      if (!user) {
        setIsFavorite(false);
        return;
      }
      const fav = await db.isFavorite(user.id, listing.id);
      setIsFavorite(fav);
    };
    void load();
  }, [user, listing.id]);
 

  if (compact) {
    return (
      <a
        href={`/priestor/${listing.slug || listing.id}/`}
        className="bg-white dark:bg-stone-900 border-b md:border border-stone-100 md:border-stone-200 dark:border-stone-800 md:rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-all duration-300 cursor-pointer flex flex-row md:flex-col md:h-full [-webkit-tap-highlight-color:transparent] p-2 md:p-0 gap-3 md:gap-0"
        onClick={() => trackEvent('listing_detail_opened', { listingId: listing.id, from: window.location.pathname })}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Left image on mobile, top image on desktop */}
        <div className="relative w-20 h-20 sm:w-24 sm:h-24 md:w-full md:h-auto md:aspect-[4/3] rounded-lg md:rounded-none bg-stone-50 dark:bg-stone-800 overflow-hidden shrink-0">
          {hasValidImages ? (
            <>
              {/* Always render the first image for immediate visibility */}
              <SmartImage
                src={getOptimizedUrl(validImages[0])}
                blurHash={listing.blurHashes?.[0]}
                alt={listing.title}
                width={400}
                height={300}
                sizes="(min-width: 768px) 400px, 96px"
                seoContext={{
                  title: listing.title,
                  category: listing.category,
                  location: listing.location,
                  index: 0
                }}
                priority={priority}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${currentImageIndex === 0 ? 'opacity-100' : 'opacity-0'
                  }`}
                containerClassName="absolute inset-0 w-full h-full"
              />
              {/* Only render secondary images when hovered to save bandwidth */}
              {isHovered && validImages.slice(1).map((img, idx) => {
                const index = idx + 1;
                return (
                  <SmartImage
                    key={index}
                    src={getOptimizedUrl(img)}
                    blurHash={listing.blurHashes?.[index]}
                    alt={listing.title}
                    width={400}
                    height={300}
                    sizes="(min-width: 768px) 400px, 96px"
                    seoContext={{
                      title: listing.title,
                      category: listing.category,
                      location: listing.location,
                      index: index
                    }}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${index === currentImageIndex ? 'opacity-100' : 'opacity-0'
                      }`}
                    containerClassName="absolute inset-0 w-full h-full"
                  />
                );
              })}
            </>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-stone-50 dark:bg-stone-800">
              <img src={`${window.location.origin}/external-gallery-placeholder.jpg`} className="w-full h-full object-cover opacity-80" alt={`${listing.title} – ${displayLocation}`} width={400} height={300} loading="lazy" decoding="async" />
              <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                <div className="text-center text-white">
                  {hasGalleryUrl ? (
                    <>
                      <Globe size={16} className="mx-auto mb-0.5" />
                      <p className="text-[7px] font-bold uppercase tracking-wider bg-black/40 px-1 rounded-full">{t('listing:open_gallery')}</p>
                    </>
                  ) : (
                    <Camera size={16} className="mx-auto opacity-50" />
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Compare Checkbox — the 2-col mobile grid leaves ~165px per card,
              so the chip must stay tiny: checkbox-only below sm (label via
              title/aria), text from sm up. No forced 44px box — the padded
              label keeps a usable tap area without covering the photo. */}
          {onCompareToggle && (
            <div className="absolute top-0 left-0 z-30" onClick={(e) => e.stopPropagation()}>
              <label
                title="Porovnať"
                className="flex items-center justify-center gap-1 bg-white/90 dark:bg-stone-900/90 backdrop-blur-md p-1.5 sm:px-2 sm:py-1.5 min-w-[36px] min-h-[36px] m-1 rounded-lg shadow-sm cursor-pointer border border-stone-200 dark:border-stone-800 hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors touch-manipulation"
              >
                <input
                  type="checkbox"
                  checked={isComparing}
                  onChange={() => onCompareToggle(listing.id)}
                  aria-label="Porovnať priestor"
                  className="w-4 h-4 rounded border-stone-300 text-accent-terra focus:ring-accent-terra cursor-pointer"
                />
                <span className="hidden sm:inline text-[9px] font-black uppercase text-stone-600 dark:text-stone-300 tracking-tight">Porovnať</span>
              </label>
            </div>
          )}

          <div className="absolute top-1 right-1 flex flex-col gap-0.5 z-10">
            {listing.isFeatured && (
              <span className="btn-primary-gradient text-white text-[7px] font-bold px-1 py-0.5 rounded-full uppercase tracking-wider flex items-center gap-0.5 w-max">
                <Zap size={6} fill="currentColor" />
              </span>
            )}
            {listing.spaceDetails?.matterportUrl && (
              <span className="bg-stone-900/85 text-white text-[7px] font-bold px-1 py-0.5 rounded-full uppercase tracking-wider flex items-center gap-0.5 w-max shadow-sm">
                <Box size={6} /> 3D
              </span>
            )}
            {isAiReady && (
              <span className="bg-white/90 dark:bg-stone-900/90 text-accent-terra dark:text-accent-terra text-[7px] font-black px-1 py-0.5 rounded-full uppercase tracking-tight flex items-center gap-0.5 w-max shadow-sm border border-stone-100 dark:border-stone-800">
                <Sparkles size={6} className="fill-accent-terra/20 text-accent-terra" /> AI
              </span>
            )}
          </div>

          <div className="absolute bottom-1 left-1 z-20">
            {listing.spaceDetails?.logoUrl && (
              <div className={`p-0.5 rounded shadow-sm flex items-center justify-center backdrop-blur-md ${listing.spaceDetails.logoMode === 'dark' ? 'bg-stone-900/80' : 'bg-white/90'}`}>
                <img src={listing.spaceDetails.logoUrl} alt={t('add_listing:media.logo_title')} className="h-4 max-w-[40px] object-contain" />
              </div>
            )}
          </div>

          {/* Desktop-only Price overlay on image */}
          <div className="absolute bottom-1 right-1 hidden md:block">
            <div className="bg-white/90 dark:bg-stone-900/90 backdrop-blur-sm text-primary dark:text-accent-terra font-bold text-[10px] sm:text-xs px-2 py-0.5 rounded-md shadow-sm whitespace-nowrap">
              {priceLabel}
            </div>
          </div>

          {features.get().favoritesEnabled && (
            <button
              className="absolute top-0 right-0 p-3 min-w-[44px] min-h-[44px] flex items-center justify-center text-white/90 hover:text-white hover:scale-110 transition-all z-20"
              onClick={async (e) => {
                e.stopPropagation();
                e.preventDefault();
                if (!user) {
                  alert(t('listing:fav_login_prompt'));
                  return;
                }
                if (isFavorite) {
                  await db.removeFavorite(user.id, listing.id);
                  setIsFavorite(false);
                } else {
                  await db.addFavorite(user.id, listing.id);
                  setIsFavorite(true);
                }
              }}
            >
              <Heart size={12} className={isFavorite ? "fill-accent-terra text-accent-terra" : "drop-shadow-md"} />
            </button>
          )}
        </div>

        {/* Content column: side on mobile, bottom on desktop */}
        <div className="flex-grow min-w-0 flex flex-col justify-between py-0.5 md:p-2">
          <div className="min-w-0">
            {/* Desktop title reserves two lines so one-line and two-line
                names leave the metadata and price rows at the same height. */}
            <h3 className="font-bold text-stone-900 dark:text-stone-200 text-xs sm:text-sm md:text-[13px] leading-tight md:leading-snug line-clamp-1 md:line-clamp-2 md:min-h-[2.25rem] mb-0.5 md:mb-1">
              {listing.title}
            </h3>

            {/* Mobile metadata row (Inline & truncated) */}
            <div className="flex md:hidden items-center text-[10px] text-stone-500 dark:text-stone-400 gap-1.5 truncate mb-1">
              <span className="truncate">{displayLocation}</span>
              <span className="text-stone-300 dark:text-stone-700 font-bold shrink-0">·</span>
              <span className="shrink-0">{t(`listing:categories.${CATEGORY_CONFIG[listing.category as Category]?.id || listing.category}`, { defaultValue: listing.category })}</span>
              {listing.spaceDetails?.maxGuests && (
                <>
                  <span className="text-stone-300 dark:text-stone-700 font-bold shrink-0">·</span>
                  <span className="shrink-0 flex items-center gap-0.5">
                    <Users size={8} /> {listing.spaceDetails.maxGuests} os.
                  </span>
                </>
              )}
            </div>

            {/* Desktop metadata row */}
            <div className="hidden md:flex items-center justify-between text-[10px] sm:text-xs font-medium text-stone-500 dark:text-stone-400 mb-1">
              <div className="flex items-center truncate">
                <MapPin size={10} className="mr-0.5 text-stone-500 dark:text-stone-400" />
                <span className="truncate">{displayLocation}</span>
              </div>
              {listing.spaceDetails?.googleRating !== undefined && listing.spaceDetails.googleRating > 0 && (
                <div className="flex items-center gap-0.5 shrink-0 bg-stone-50 dark:bg-stone-800 border border-stone-100 dark:border-stone-800 px-1 py-0.5 rounded">
                  <Star size={10} className="text-amber-500 fill-amber-500" />
                  <span className="font-bold text-[10px] text-stone-700 dark:text-stone-300">{listing.spaceDetails.googleRating.toFixed(1)}</span>
                </div>
              )}
            </div>

            {/* Desktop capacity */}
            {listing.spaceDetails?.maxGuests && (
              <div className="hidden md:flex items-center text-[8px] sm:text-[9px] text-stone-600 dark:text-stone-400">
                <Users size={8} className="mr-0.5" /> {t('listing:capacity', { count: listing.spaceDetails.maxGuests })}
              </div>
            )}
          </div>

          {/* Mobile Price & Rating row */}
          <div className="flex items-center justify-between mt-auto">
            <span className="text-xs font-extrabold text-accent-terra dark:text-accent-terra">
              {priceLabel}
            </span>
            {listing.spaceDetails?.googleRating !== undefined && listing.spaceDetails.googleRating > 0 && (
              <div className="flex md:hidden items-center gap-0.5 bg-stone-50 dark:bg-stone-800 border border-stone-100 dark:border-stone-800 px-1 py-0.5 rounded text-[9px] font-bold text-stone-700 dark:text-stone-300 shrink-0">
                <Star size={8} className="text-amber-500 fill-amber-500" />
                <span>{listing.spaceDetails.googleRating.toFixed(1)}</span>
              </div>
            )}
          </div>
        </div>
      </a>
    );
  }

  const containerClass = listing.isFeatured
    ? "bg-white dark:bg-stone-900 rounded-3xl sm:rounded-2xl border border-stone-200 dark:border-accent-terra/30 shadow-lg shadow-accent-terra/5 overflow-hidden transition-all duration-300 group cursor-pointer relative"
    : "bg-white dark:bg-stone-900 rounded-3xl sm:rounded-2xl border border-stone-200 dark:border-stone-800 shadow-sm hover:shadow-md hover:border-stone-300 dark:hover:border-stone-700 transition-all duration-300 group cursor-pointer relative";

  let href = `/priestor/${listing.slug || listing.id}/`;
  let target = "_self";
  let rel = "";

  // Only redirect directly to external gallery if there are NO real images at all
  if (!hasValidImages && hasGalleryUrl) {
    let finalUrl = listing.spaceDetails!.externalGalleryUrl!;
    if (!finalUrl.startsWith('http://') && !finalUrl.startsWith('https://')) {
      finalUrl = 'https://' + finalUrl;
    }
    href = finalUrl;
    target = "_blank";
    rel = "noopener noreferrer";
  }

  return (
    <a
      href={href}
      target={target}
      rel={rel}
      className={`${containerClass} block [-webkit-tap-highlight-color:transparent]`}
      onClick={() => trackEvent('listing_detail_opened', { listingId: listing.id, from: window.location.pathname, external: target === '_blank' })}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="relative aspect-[4/3] bg-stone-50 dark:bg-stone-800 overflow-hidden">
        {hasValidImages ? (
          <>
            <SmartImage
              src={getOptimizedUrl(validImages[0])}
              blurHash={listing.blurHashes?.[0]}
              alt={`${listing.title} - foto 1`}
              width={800}
              height={600}
              seoContext={{
                title: listing.title,
                category: listing.category,
                location: listing.location,
                index: 0
              }}
              priority={priority}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-in-out ${currentImageIndex === 0 ? 'opacity-100' : 'opacity-0'
                }`}
              containerClassName="absolute inset-0 w-full h-full"
            />
            {isHovered && validImages.slice(1).map((img, idx) => {
              const index = idx + 1;
              return (
                <SmartImage
                  key={index}
                  src={getOptimizedUrl(img)}
                  blurHash={listing.blurHashes?.[index]}
                  alt={`${listing.title} - foto ${index + 1}`}
                  width={800}
                  height={600}
                  seoContext={{
                    title: listing.title,
                    category: listing.category,
                    location: listing.location,
                    index: index
                  }}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-in-out ${index === currentImageIndex ? 'opacity-100' : 'opacity-0'
                    }`}
                  containerClassName="absolute inset-0 w-full h-full"
                />
              );
            })}
          </>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-stone-50 dark:bg-stone-800">
            <img src={`${window.location.origin}/external-gallery-placeholder.jpg`} className="w-full h-full object-cover opacity-80" alt={`${listing.title} – ${displayLocation}`} width={800} height={600} loading="lazy" decoding="async" />
            <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors flex items-center justify-center">
              <div className="text-center text-white p-4">
                {hasGalleryUrl ? (
                  <>
                    <Globe size={48} className="mx-auto mb-3 drop-shadow-lg group-hover:scale-110 transition-transform" />
                    <p className="text-xs sm:text-sm font-bold uppercase tracking-widest bg-black/50 backdrop-blur-md px-6 py-2.5 rounded-full border border-white/20 shadow-2xl">
                      {t('listing:open_gallery')}
                    </p>
                  </>
                ) : (
                  <Camera size={48} className="mx-auto mb-3 opacity-30 drop-shadow-lg" />
                )}
              </div>
            </div>
          </div>
        )}

        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-60"></div>

        <div className="absolute top-2 left-2 sm:top-3 sm:left-3 flex flex-col gap-1.5 z-10 w-full pr-6">
          {listing.isFeatured && (
            <span className="self-start btn-primary-gradient text-white text-[9px] sm:text-[10px] font-bold px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full uppercase tracking-wider flex items-center gap-1 shadow-sm">
              <Zap size={10} fill="currentColor" /> {t('listing:premium')}
            </span>
          )}
          {listing.spaceDetails?.matterportUrl && (
            <span className="self-start bg-stone-900/85 text-white text-[9px] sm:text-[10px] font-bold px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full uppercase tracking-wider flex items-center gap-1 shadow-md">
              <Box size={10} /> 3D pohľad
            </span>
          )}
          {isAiReady && (
            <span className="self-start bg-white/95 dark:bg-stone-900/95 text-accent-terra dark:text-accent-terra text-[9px] sm:text-[10px] font-black px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full uppercase tracking-wider flex items-center gap-1 shadow-md border border-stone-100 dark:border-stone-800">
              <Sparkles size={10} className="fill-accent-terra/10 text-accent-terra" /> AI READY
            </span>
          )}
        </div>

        <div className="absolute top-3 right-3 flex flex-col gap-1 items-end z-20 hover:scale-105 transition-transform duration-300 pointer-events-none">
        </div>

        {listing.spaceDetails?.logoUrl && (
          <div className="absolute bottom-3 left-3 z-20 hover:scale-105 transition-transform duration-300">
            <div className={`p-1.5 rounded-lg shadow-md flex items-center justify-center backdrop-blur-md ${listing.spaceDetails.logoMode === 'dark' ? 'bg-stone-900/80' : 'bg-white/90'}`}>
              <img src={listing.spaceDetails.logoUrl} alt={t('add_listing:media.logo_title')} className="h-6 sm:h-8 max-w-[80px] sm:max-w-[100px] object-contain" />
            </div>
          </div>
        )}

        <div className="absolute bottom-2 right-2 sm:bottom-3 sm:right-3 z-20 hover:scale-105 transition-transform duration-300">
          <div className="bg-white/90 dark:bg-stone-900/90 backdrop-blur-md text-primary dark:text-accent-terra font-bold text-[11px] sm:text-sm px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-full shadow-lg border border-white/50 dark:border-stone-700/50">
            {priceLabel}
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6 flex flex-col flex-1">
        <div className="flex justify-between items-start gap-2 mb-2 sm:mb-3 min-h-[3.5rem] sm:min-h-[4rem]">
          <h3 className="font-black text-primary dark:text-stone-100 text-lg sm:text-2xl leading-tight group-hover:text-accent-terra dark:group-hover:text-accent-terra transition-colors line-clamp-2">
            {listing.title}
          </h3>

          {features.get().favoritesEnabled && (
            <button
              className="shrink-0 -mr-2 -mt-2 p-3 min-w-[44px] min-h-[44px] flex items-center justify-center text-stone-500 hover:text-accent-terra dark:hover:text-accent-terra transition-colors"
              onClick={async (e) => {
                e.stopPropagation();
                if (!user) {
                  alert(t('listing:fav_login_prompt'));
                  return;
                }
                if (isFavorite) {
                  await db.removeFavorite(user.id, listing.id);
                  setIsFavorite(false);
                } else {
                  await db.addFavorite(user.id, listing.id);
                  setIsFavorite(true);
                }
              }}
            >
              <Heart size={18} className={isFavorite ? "fill-accent-terra text-accent-terra" : ""} />
            </button>
          )}

          {onCompareToggle && (
            <div className="absolute top-2 left-2 sm:top-3 sm:left-3 z-30" onClick={(e) => e.stopPropagation()}>
              <label className="flex items-center gap-1.5 bg-white/95 dark:bg-stone-900/95 backdrop-blur-md px-2 py-1 sm:px-3 sm:py-1.5 rounded-xl shadow-md cursor-pointer border border-stone-200 dark:border-stone-800 hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors">
                <input
                  type="checkbox"
                  checked={isComparing}
                  onChange={() => onCompareToggle(listing.id)}
                  className="w-3 h-3 sm:w-4 sm:h-4 rounded border-stone-300 text-accent-terra focus:ring-accent-terra"
                />
                <span className="text-[10px] sm:text-xs font-black uppercase text-stone-700 dark:text-stone-200 tracking-wider">Porovnať</span>
              </label>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between text-xs sm:text-[15px] font-medium text-stone-500 dark:text-stone-400 mb-3 sm:mb-4">
          <div className="flex items-center truncate">
            <MapPin size={14} className="mr-1 sm:mr-1.5 text-stone-500 dark:text-stone-400 shrink-0" />
            <span className="truncate">{displayLocation}</span>
          </div>
          {listing.spaceDetails?.googleRating !== undefined && listing.spaceDetails.googleRating > 0 && (
            <div className="flex items-center gap-1 sm:gap-1.5 shrink-0 bg-stone-50 dark:bg-stone-800 border border-stone-100 dark:border-stone-700 px-2 py-1 sm:px-2.5 sm:py-1.5 rounded-lg sm:rounded-xl shadow-sm" title={`${listing.spaceDetails.googleRatingsCount} Google recenzií`}>
              <Star size={14} className="text-amber-500 fill-amber-500" />
              <span className="font-bold text-[11px] sm:text-sm text-stone-700 dark:text-stone-300">{listing.spaceDetails.googleRating.toFixed(1)}</span>
            </div>
          )}
        </div>

        {(listing.spaceDetails?.maxGuests || listing.spaceDetails?.parkingAvailable) && (
          <div className="flex flex-wrap gap-2 mb-4">
            {listing.spaceDetails?.maxGuests && (
              <span className="px-2 py-1 bg-stone-100 dark:bg-stone-800 rounded-md text-[10px] font-medium text-stone-600 dark:text-stone-300 flex items-center">
                <Users size={10} className="mr-1" /> {t('listing:capacity', { count: listing.spaceDetails.maxGuests })}
              </span>
            )}
            {listing.spaceDetails?.parkingAvailable && (
              <span className="px-2 py-1 bg-stone-100 dark:bg-stone-800 rounded-md text-[10px] font-medium text-stone-600 dark:text-stone-300 flex items-center">
                <Car size={10} className="mr-1" /> {t('listing:parking')}
              </span>
            )}
          </div>
        )}

        <div className="pt-3 border-t border-stone-100 dark:border-stone-800 flex items-center justify-between text-[10px] sm:text-xs text-stone-500 dark:text-stone-400">
          <span>{t(`listing:categories.${CATEGORY_CONFIG[listing.category as Category]?.id || listing.category}`, { defaultValue: listing.category })}</span>
          <span className="flex items-center gap-1">
            <Clock size={10} /> {formatListingDate(listing.createdAt)}
          </span>
        </div>
      </div>
    </a>
  );
};

export const ListingCard = memo(ListingCardComponent);
